import { Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Collection,
  composeRenderProps,
  Header,
  ListBoxSection,
} from 'react-aria-components';

export function Dropdown({ label, items, onSelect, className, disabled }) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!menuRef.current || !btnRef.current) return;
      if (!menuRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = useCallback(
    (item) => {
      if (!item.disabled) {
        onSelect(item);
        setOpen(false);
        setHighlightedIndex(-1);
        btnRef.current?.focus();
      }
    },
    [onSelect],
  );

  const onKeyDownList = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev + 1 >= items.length ? 0 : prev + 1;
          return next;
        });
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev - 1 < 0 ? items.length - 1 : prev - 1;
          return next;
        });
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          handleSelect(items[highlightedIndex]);
        }
      }

      if (e.key === 'Home') {
        e.preventDefault();
        setHighlightedIndex(0);
      }

      if (e.key === 'End') {
        e.preventDefault();
        setHighlightedIndex(items.length - 1);
      }
    },
    [open, items, highlightedIndex, handleSelect],
  );

  useEffect(() => {
    if (open && items.length > 0) {
      setHighlightedIndex((prev) => (prev === -1 ? 0 : prev));
    }
  }, [open, items.length]);

  useEffect(() => {
    if (open && highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].focus();
    }
  }, [highlightedIndex, open]);

  const handleButtonKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex(() => {
        if (e.key === 'ArrowDown') {
          return 0;
        }
        return items.length - 1;
      });
    }
  };

  return (
    <div className={clsx('relative inline-block text-left text-body', className)}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="p-2.5 pr-8.5 text-small bg-white text-gray-500 rounded-md font-normal disabled:opacity-50 disabled:pointer-events-none"
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        onKeyDown={handleButtonKeyDown}
        disabled={disabled}
      >
        <span>{label}</span>
        <span
          className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5"
          aria-hidden="true"
        >
          <ChevronDownIcon className="size-3.75 text-gray-500" />
        </span>
      </button>

      <Transition
        show={open}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ul
          ref={menuRef}
          role="listbox"
          tabIndex={-1}
          onKeyDown={onKeyDownList}
          className="absolute z-50 mt-[1px] max-h-[225px] w-full min-w-[310px] overflow-auto rounded shadow-lg bg-white py-1.25 caption font-medium"
        >
          {items.map((item, idx) => (
            <li
              ref={(el) => (itemRefs.current[idx] = el)}
              className={clsx(
                'p-2.5 mb-1.25 transition-colors cursor-pointer last:mb-0 data-[disabled=false]:hover:bg-primary-100 data-[disabled=false]:active:bg-primary-100 data-[disabled=true]:opacity-50 data-[disabled=true]:cursor-default',
                // {
                //   'bg-primary-100': item.isSelected || highlightedIndex === idx,
                // },
              )}
              key={item.value}
              role="option"
              aria-selected={highlightedIndex === idx || item.isSelected}
              tabIndex={-1}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              data-disabled={item.disabled}
            >
              {item.label}
            </li>
          ))}
        </ul>
      </Transition>
    </div>
  );
}

export function ListBox({ children, ...props }) {
  return (
    <AriaListBox
      {...props}
      className="py-1 empty:p-0 outline-0 w-full bg-white border rounded box-border max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0_round_.25rem)]"
    >
      {children}
    </AriaListBox>
  );
}

export const itemStyles = {
  extend: 'focusRing',
  base: 'group relative flex items-center gap-8 cursor-default select-none py-2 px-3',
  variants: {
    isSelected: {
      false:
        'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 pressed:bg-neutral-100 dark:hover:bg-neutral-800 dark:pressed:bg-neutral-800 -outline-offset-2',
      true: 'bg-blue-600 text-white forced-colors:bg-[Highlight] forced-colors:text-[HighlightText] [&:has(+[data-selected])]:rounded-b-none [&+[data-selected]]:rounded-t-none -outline-offset-4 outline-white dark:outline-white forced-colors:outline-[HighlightText]',
    },
    isDisabled: {
      true: 'text-neutral-300 dark:text-neutral-600 forced-colors:text-[GrayText]',
    },
  },
};

export function ListBoxItem({ children, ...props }) {
  let textValue =
    props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaListBoxItem {...props} textValue={textValue} className={itemStyles.base}>
      {children}
    </AriaListBoxItem>
  );
}

export function DropdownItem(props) {
  return (
    <AriaListBoxItem className="group/item" {...props}>
      {composeRenderProps(props.children, (children) => (
        <span className="flex items-center flex-1 gap-2 text-base font-normal truncate px-2 py-1.5 group-data-[hovered=true]/item:bg-primary-500 group-data-[hovered=true]/item:text-white group-data-[focused=true]/item:bg-primary-500 group-data-[focused=true]/item:text-white">
          {children}
        </span>
      ))}
    </AriaListBoxItem>
  );
}

export function DropdownSection({ title, items, children }) {
  return (
    <ListBoxSection className="after:content-[''] after:block after:h-[5px] last:after:hidden">
      <Header className="text-base font-semibold truncate px-2 py-1.5 [&+*]:mt-1">{title}</Header>
      <Collection items={items}>{children}</Collection>
    </ListBoxSection>
  );
}
